import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "../../components/public/Seo";
import { createEditMeta } from "../../state/editsStore";
import { setActiveEditId } from "../../state/activeEditStore";
import { addToEdit } from "../../state/editStore";
import { apiFetch } from "../../lib/api";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  // New CMS uploads live on backend
  if (url.startsWith("/tryon-assets/")) {
    return `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}${url}`;
  }

  // Legacy Veya edits live in frontend public/
  return url;
}

function seedEditFromVeyaEdit(v) {
  const meta = createEditMeta({ name: v.title, templateSlug: v.slug });
  const editId = meta.id;

  for (const imageUrl of v.images || []) {
    addToEdit(
      {
        id: uid(),
        createdAt: Date.now(),
        kind: "image",
        title: v.title,
        imageUrl: resolveImageUrl(imageUrl),
        sourceUrl: null,
      },
      editId
    );
  }

  for (const note of v.notes || []) {
    const text = String(note || "").trim();
    if (!text) continue;

    addToEdit(
      {
        id: uid(),
        createdAt: Date.now(),
        kind: "note",
        title: "Note",
        text,
        sourceUrl: null,
      },
      editId
    );
  }

  setActiveEditId(editId);
  return editId;
}

function NotesChips({ notes }) {
  if (!notes?.length) return null;

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
      {notes.slice(0, 10).map((t, idx) => (
        <span
          key={`note_${idx}`}
          className="public-muted"
          style={{
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 999,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "rgba(0,0,0,0.03)",
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function ImageQuad({ images }) {
  const four = (images || []).slice(0, 4);

  return (
    <div
      style={{
        marginTop: 10,
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 10,
      }}
    >
      {(four.length ? four : [null, null, null, null]).map((url, idx) => (
        <div
          key={`${url || "empty"}_${idx}`}
          style={{
            width: "100%",
            aspectRatio: "4 / 5",
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            background: "rgba(0,0,0,0.05)",
          }}
        >
          {url ? (
            <img
  src={optimizedImageUrl(resolveImageUrl(url), { width: 520, quality: 78 })}
  alt=""
  loading="lazy"
  decoding="async"
  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
/>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function MoodsIndex() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const data = await apiFetch("/veya-team/edits/public");
setCards(Array.isArray(data) ? data : []);

      } catch (err) {
        console.error("Failed to load public Veya edits", err);
        setError(err?.message || "Could not load Veya edits.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  function saveAndEdit(ev, v) {
    ev.preventDefault();
    ev.stopPropagation();

    const id = seedEditFromVeyaEdit(v);
    window.location.href = `/fitting-room?openedit=1&editId=${encodeURIComponent(id)}`;
  }

  return (
    <>
      <Seo
        title="Veya — Veya’s Edits"
        description="Pre-curated edits you can save, personalize, and evolve."
      />

      <div className="public-breadcrumb">
        <span className="public-muted">Moods</span>
        <span className="public-muted"> / </span>
        <span>Veya’s Edits</span>
      </div>

      <h1 className="public-h1">Veya’s Edits</h1>
      <p className="public-lede">
        Pre-curated starting points. Save one, then edit it however you like — add screenshots, items from the catalog and notes. Then use these to search for similar items to build your wardrobe.
      </p>

      {loading ? <p className="public-muted">Loading Veya edits...</p> : null}
      {error ? <p className="merchant-error">{error}</p> : null}

      {!loading && !error && cards.length === 0 ? (
        <p className="public-muted">No live Veya edits yet.</p>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 12,
        }}
      >
        {cards.map((v) => (
          <div
            key={v.slug}
            className="public-card"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/moods/${encodeURIComponent(v.slug)}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                navigate(`/moods/${encodeURIComponent(v.slug)}`);
              }
            }}
            style={{
              textAlign: "left",
              padding: 0,
              overflow: "hidden",
              cursor: "pointer",
            }}
          >
            <div style={{ padding: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 20, lineHeight: 1.15 }}>{v.title}</div>
              <div className="public-muted" style={{ marginTop: 6, lineHeight: 1.25 }}>
                {v.subtitle}
              </div>

              <ImageQuad images={v.images} />

              <NotesChips notes={v.notes || []} />

              <div style={{ marginTop: 12 }}>
                <button className="public-cta" type="button" onClick={(ev) => saveAndEdit(ev, v)}>
                  Save & edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}